import React, { useMemo } from 'react';
import type { CalendarDayView, CalendarSlot } from '../../../types';

// Booking status styling helper
const getBookingStyles = (status: string | undefined, isConflict: boolean = false) => {
  const isPending = status === 'pending';

  if (isConflict) {
    return {
      isPending: false,
      isConflict: true,
      mobile: {
        container: 'bg-red-50 border-red-400',
      },
      desktop: {
        container: 'border-red-400 bg-red-50 hover:bg-red-100',
        durationText: 'text-red-600',
      },
    };
  }

  return {
    isPending,
    isConflict: false,
    // Mobile list item styles
    mobile: {
      container: isPending
        ? 'bg-gradient-to-r from-purple-50 via-white to-purple-50 border-purple-300 border-dashed'
        : 'bg-purple-50 border-purple-300',
    },
    // Desktop styles
    desktop: {
      container: isPending
        ? 'border-purple-300 border-dashed bg-[repeating-linear-gradient(135deg,#faf5ff,#faf5ff_4px,#f3e8ff_4px,#f3e8ff_8px)] hover:bg-[repeating-linear-gradient(135deg,#f3e8ff,#f3e8ff_4px,#ede9fe_4px,#ede9fe_8px)]'
        : 'border-purple-400 bg-purple-50 hover:bg-purple-100',
      durationText: isPending ? 'text-purple-500' : 'text-purple-600',
    },
  };
};

interface DayViewProps {
  data: CalendarDayView;
  onSlotClick?: (slot: CalendarSlot, date: string) => void;
  onBookingClick?: (bookingId: string) => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onGoToToday?: () => void;
}

interface ProcessedBooking {
  id: string;
  startSlotIndex: number;
  slotSpan: number;
  visualSpan: number; // Rounded up to nearest hour for display
  slot: CalendarSlot;
}

// Calculate visual span from time_range, rounded up to nearest hour
const calculateVisualSpan = (timeRange: string | undefined, fallbackSpan: number): number => {
  if (!timeRange) return Math.max(1, fallbackSpan);

  // Parse time_range like "15:00-15:45" or "09:00-11:00"
  const match = timeRange.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
  if (!match) return Math.max(1, fallbackSpan);

  const [, startHour, startMin, endHour, endMin] = match;
  const startMinutes = parseInt(startHour) * 60 + parseInt(startMin);
  const endMinutes = parseInt(endHour) * 60 + parseInt(endMin);
  const durationMinutes = endMinutes - startMinutes;

  // Round up to nearest hour (60 minutes)
  const visualHours = Math.ceil(durationMinutes / 60);
  return Math.max(1, visualHours);
};

export const DayView: React.FC<DayViewProps> = ({
  data,
  onSlotClick,
  onBookingClick,
  onNavigatePrevious,
  onNavigateNext,
  onGoToToday,
}) => {
  // Get weekday abbreviation and day number for mobile header
  const dateObj = new Date(data.date);
  const weekdayShort = dateObj.toLocaleDateString('en-US', { weekday: 'short' }).substring(0, 2);
  const dayNumber = dateObj.getDate();

  // Process slots to identify bookings and their spans
  const { slotBookingMap, uniqueBookings } = useMemo(() => {
    const bookings: ProcessedBooking[] = [];
    const bookingMap = new Map<number, ProcessedBooking>(); // slotIndex -> booking (for first slot only)
    const seenBookingIds = new Set<string>();
    const unique: ProcessedBooking[] = [];

    data.slots.forEach((slot, index) => {
      if ((slot.status === 'booked' || slot.status === 'conflict') && slot.booking) {
        if (!seenBookingIds.has(slot.booking.id)) {
          // First occurrence of this booking
          seenBookingIds.add(slot.booking.id);

          // Count how many consecutive slots this booking spans
          let span = 1;
          for (let i = index + 1; i < data.slots.length; i++) {
            const nextSlot = data.slots[i];
            if ((nextSlot.status === 'booked' || nextSlot.status === 'conflict') && nextSlot.booking?.id === slot.booking.id) {
              span++;
            } else {
              break;
            }
          }

          // Calculate visual span from time_range (rounded up to nearest hour)
          const visualSpan = calculateVisualSpan(slot.booking.time_range, span);

          const processedBooking: ProcessedBooking = {
            id: slot.booking.id,
            startSlotIndex: index,
            slotSpan: span,
            visualSpan: visualSpan,
            slot: slot,
          };

          bookings.push(processedBooking);
          unique.push(processedBooking);
          bookingMap.set(index, processedBooking);
        }
      }
    });

    return { slotBookingMap: bookingMap, uniqueBookings: unique };
  }, [data.slots]);

  // For mobile - filter to show available slots, no_service_fit slots, and unique bookings
  const mobileItems = useMemo(() => {
    const items: Array<{ type: 'available' | 'booking' | 'conflict' | 'no_service_fit'; slot: CalendarSlot; booking?: ProcessedBooking }> = [];
    const processedIndices = new Set<number>();

    data.slots.forEach((slot, index) => {
      if (processedIndices.has(index)) return;

      if (slot.status === 'available') {
        items.push({ type: 'available', slot });
      } else if (slot.status === 'no_service_fit') {
        items.push({ type: 'no_service_fit', slot });
      } else if ((slot.status === 'booked' || slot.status === 'conflict') && slot.booking) {
        const booking = slotBookingMap.get(index);
        if (booking) {
          items.push({ type: slot.status === 'conflict' ? 'conflict' : 'booking', slot, booking });
          // Mark all slots of this booking as processed (use visualSpan for consistent layout)
          for (let i = 0; i < booking.visualSpan; i++) {
            processedIndices.add(index + i);
          }
        }
      }
    });

    return items;
  }, [data.slots, slotBookingMap]);

  const handleSlotClick = (slot: CalendarSlot) => {
    if ((slot.status === 'booked' || slot.status === 'conflict') && slot.booking) {
      onBookingClick?.(slot.booking.id);
    } else if (slot.status === 'available') {
      onSlotClick?.(slot, data.date);
    }
  };

  // Calculate duration text from visual span
  const getDurationText = (visualSpan: number) => {
    if (visualSpan === 1) return '1 hour';
    return `${visualSpan} hours`;
  };

  return (
    <>
      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* Day Header */}
        <div className="text-center mb-4">
          <div className="text-sm font-medium text-gray-500 uppercase">{weekdayShort}</div>
          <div className="text-2xl font-bold text-gray-900">{dayNumber}</div>
        </div>

        {/* Day off banner with conflict warning */}
        {data.is_day_off && data.slots.length > 0 && (
          <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800">Day Off - Booking Conflict</p>
                <p className="text-xs text-red-600">{data.slots.length} booking{data.slots.length > 1 ? 's' : ''} on a day off</p>
              </div>
            </div>
          </div>
        )}

        {/* Time Slots List */}
        {mobileItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg
              className="mx-auto h-10 w-10 text-gray-400 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-base font-medium">No available slots</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {mobileItems.map((item, index) => {
              const isConflict = item.type === 'conflict';
              const isBookingOrConflict = item.type === 'booking' || isConflict;
              const styles = isBookingOrConflict && item.slot.booking
                ? getBookingStyles(item.slot.booking.status, isConflict)
                : null;

              return (
                <div
                  key={index}
                  className={`flex items-center p-3 rounded-xl border transition-colors shadow-sm ${
                    isBookingOrConflict && styles
                      ? `${styles.mobile.container} ${isConflict ? 'active:bg-red-100' : 'active:bg-purple-100'} cursor-pointer`
                      : item.type === 'no_service_fit'
                      ? 'bg-gray-50 border-gray-200 opacity-60'
                      : 'bg-green-50/30 border-green-200 active:bg-green-50 cursor-pointer'
                  }`}
                  onClick={() => item.type !== 'no_service_fit' && handleSlotClick(item.slot)}
                >
                  {/* Time */}
                  <div className="w-20 flex-shrink-0">
                    {isBookingOrConflict && item.booking && styles ? (
                      <div className="leading-tight">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.slot.booking?.time_range || item.slot.time}
                        </span>
                        <div className={`text-xs font-medium ${isConflict ? 'text-red-600' : styles.isPending ? 'text-purple-500' : 'text-purple-600'}`}>
                          {isConflict ? '⚠️ ' : styles.isPending ? '⏳ ' : ''}{getDurationText(item.booking.visualSpan)}
                        </div>
                      </div>
                    ) : (
                      <span className={`text-sm font-semibold ${item.type === 'no_service_fit' ? 'text-gray-400' : 'text-gray-900'}`}>{item.slot.time}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {isBookingOrConflict && item.slot.booking ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {item.slot.booking.client.name}
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-600 truncate">
                            {item.slot.booking.service.name}
                          </span>
                        </div>
                        {isConflict && (
                          <span className="text-xs font-medium text-red-600">Conflict - Day off</span>
                        )}
                      </div>
                    ) : item.type === 'no_service_fit' ? (
                      <span className="text-sm text-gray-400">No services fit this time slot</span>
                    ) : (
                      <span className="text-sm text-green-600 font-medium">Available</span>
                    )}
                  </div>

                  {/* Arrow indicator */}
                  {item.type !== 'no_service_fit' && (
                    <div className="flex-shrink-0 ml-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop Layout - Timeline View */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                {onNavigatePrevious && (
                  <button
                    onClick={onNavigatePrevious}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                    aria-label="Previous day"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {new Date(data.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </h3>
                {onNavigateNext && (
                  <button
                    onClick={onNavigateNext}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                    aria-label="Next day"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                {onGoToToday && (
                  <button
                    onClick={onGoToToday}
                    className="ml-1 px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Today
                  </button>
                )}
              </div>
              {data.is_day_off ? (
                <p className="text-sm text-red-600 font-medium mt-1">Day Off</p>
              ) : data.working_hours ? (
                <p className="text-sm text-gray-500 mt-1">
                  Working hours: {data.working_hours.start} - {data.working_hours.end}
                </p>
              ) : null}
            </div>
            <div className="flex gap-4 text-sm">
              {data.is_day_off && uniqueBookings.length > 0 ? (
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{uniqueBookings.length}</div>
                  <div className="text-red-500">Conflicts</div>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{uniqueBookings.length}</div>
                    <div className="text-gray-500">Bookings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{data.summary.available_slots}</div>
                    <div className="text-gray-500">Available</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Timeline slots */}
        <div className="p-6">
          {data.slots.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-lg font-medium">No working hours configured for this day</p>
            </div>
          ) : (
            <div className="relative">
              {/* Time grid */}
              <div className="flex flex-col gap-1">
                {data.slots.map((slot, index) => {
                  const booking = slotBookingMap.get(index);
                  const isBookingStart = booking !== undefined;
                  const isBookingContinuation = (slot.status === 'booked' || slot.status === 'conflict') && !isBookingStart;
                  const isConflictSlot = slot.status === 'conflict';

                  return (
                    <div key={index} className="relative" style={{ height: '60px' }}>
                        {isBookingStart && booking && slot.booking ? (
                          // Booking card - row-based layout matching weekly view style
                          (() => {
                            const styles = getBookingStyles(slot.booking.status, isConflictSlot);
                            return (
                              <div
                                className={`absolute inset-x-0 top-0 border-2 rounded-lg flex items-center px-2 cursor-pointer ${isConflictSlot ? 'hover:border-red-500' : 'hover:border-purple-500'} hover:shadow-md transition-all z-10 ${styles.desktop.container}`}
                                style={{
                                  height: `${booking.visualSpan * 60 + (booking.visualSpan - 1) * 4}px`,
                                }}
                                onClick={() => onBookingClick?.(slot.booking!.id)}
                              >
                                {/* Left: Time + Duration */}
                                <div className="w-16 flex-shrink-0">
                                  <div className="text-[11px] font-semibold text-gray-900 leading-tight">
                                    {slot.booking.time_range}
                                  </div>
                                  <div className={`text-[10px] font-medium leading-tight ${styles.desktop.durationText}`}>
                                    {isConflictSlot ? '⚠️ ' : styles.isPending ? '⏳ ' : ''}{getDurationText(booking.visualSpan)}
                                  </div>
                                </div>

                                {/* Middle: Client | Service + Conflict label */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[11px] font-medium text-gray-900 truncate">
                                      {slot.booking.client.name}
                                    </span>
                                    <span className="text-gray-300 flex-shrink-0">|</span>
                                    <span className="text-[11px] text-gray-600 truncate">
                                      {slot.booking.service.name}
                                    </span>
                                    {isConflictSlot && (
                                      <span className="flex-shrink-0 text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Conflict</span>
                                    )}
                                  </div>
                                </div>

                                {/* Right: Arrow */}
                                <div className="flex-shrink-0 ml-1">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            );
                          })()
                        ) : isBookingContinuation ? (
                          // Empty space for booking continuation (booking card overlaps this)
                          null
                        ) : slot.status === 'available' ? (
                          // Available slot - row layout matching weekly view
                          <div
                            className="absolute inset-0 flex items-center px-2 rounded-lg bg-white border border-gray-200 hover:bg-green-50 hover:border-green-300 cursor-pointer transition-colors"
                            onClick={() => onSlotClick?.(slot, data.date)}
                          >
                            {/* Left: Time */}
                            <div className="w-12 flex-shrink-0">
                              <span className="text-xs font-semibold text-gray-900">{slot.time}</span>
                            </div>

                            {/* Middle: Available label */}
                            <div className="flex-1">
                              <span className="text-[11px] text-green-600 font-medium">Available</span>
                            </div>

                            {/* Right: Arrow */}
                            <div className="flex-shrink-0">
                              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        ) : slot.status === 'no_service_fit' ? (
                          // No service fits - disabled slot
                          <div className="absolute inset-0 flex items-center px-2 rounded-lg bg-gray-50 border border-gray-200 opacity-60">
                            <div className="w-12 flex-shrink-0">
                              <span className="text-xs font-semibold text-gray-400">{slot.time}</span>
                            </div>
                            <div className="flex-1">
                              <span className="text-[11px] text-gray-400">No services fit this time slot</span>
                            </div>
                          </div>
                        ) : slot.status === 'non_working' ? (
                          // Non-working slot
                          <div className="absolute inset-0 flex items-center px-2 rounded-lg bg-gray-100/50 border border-gray-200">
                            <span className="text-xs text-gray-400">Not available</span>
                          </div>
                        ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
